import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';

import { Loader2, PanelRightClose, PanelRightOpen } from 'lucide-react';

import type { PanelSectionId } from './types';

interface RailBadge {
  tone: 'warning' | 'error' | 'count' | 'done' | 'busy';
  /** Count or short text; omitted for a dot. */
  text?: string;
}

export interface RailItem {
  id: PanelSectionId;
  label: string;
  icon: JSX.Element;
  badge?: RailBadge;
  disabled?: boolean;
  /** Why the item is disabled, or extra context. */
  hint?: string;
  /** `finish`: end-of-project steps under a divider. `mode`: swaps the 3D view (Export), so it sits
   *  apart at the bottom of the rail. */
  group?: 'finish' | 'mode';
}

const BADGE: Record<RailBadge['tone'], string> = {
  warning: 'bg-amber-400 text-black',
  error: 'bg-red-500 text-white',
  count: 'bg-[#3a3a3a] text-white',
  done: 'bg-lime-400 text-black',
  busy: 'bg-transparent text-lime-400',
};

function Badge({ badge }: { badge: RailBadge }) {
  if (badge.tone === 'busy') {
    return <Loader2 size={12} className="absolute -top-1 -right-2 animate-spin text-lime-400" />;
  }
  const shape = badge.text
    ? '-top-1.5 -right-2.5 min-w-[16px] h-4 px-1 text-[10px] font-semibold leading-4'
    : '-top-0.5 -right-1 w-2 h-2';
  return (
    <span className={`absolute z-10 rounded-full text-center ring-2 ring-[#161616] ${shape} ${BADGE[badge.tone]}`}>
      {badge.text}
    </span>
  );
}

interface PanelRailProps {
  items: RailItem[];
  active: PanelSectionId;
  open: boolean;
  onSelect: (id: PanelSectionId) => void;
  onToggle: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const SHORTCUT_PREFIX = isMac ? '⌥' : 'Alt+';
const TOGGLE_SHORTCUT = isMac ? '⌥0' : 'Alt+0';

/** Slack-style icon rail on the right edge: one button per panel section. */
export function PanelRail({ items, active, open, onSelect, onToggle }: PanelRailProps) {
  const { t } = useTranslation();

  // Shortcuts follow the order of `items`, wherever the button is drawn.
  const renderItem = (item: RailItem, index: number) => {
    const isActive = open && item.id === active;
    const shortcut = `${SHORTCUT_PREFIX}${index + 1}`;
    const startsFinishGroup = item.group === 'finish' && items[index - 1]?.group !== 'finish';
    const isMode = item.group === 'mode';
    return (
      <div key={item.id} className="contents">
        {startsFinishGroup && <div aria-hidden className="my-1 h-px w-8 shrink-0 bg-white/10" />}
        <Tooltip
          placement="left"
          arrow
          title={
            <span className="block text-xs">
              <span className="font-medium">{item.label}</span>
              <span className="ml-2 opacity-60">{shortcut}</span>
              {item.hint && <span className="block mt-0.5 opacity-80">{item.hint}</span>}
            </span>
          }
        >
          <span>
            <button
              type="button"
              onClick={() => onSelect(item.id)}
              disabled={item.disabled}
              aria-pressed={isActive}
              aria-label={item.label}
              className={`group w-14 flex flex-col items-center gap-1 rounded-lg pt-2 pb-1.5 transition-colors disabled:cursor-not-allowed ${
                isActive ? 'bg-white/10 text-lime-400' : 'text-gray-300 hover:bg-white/5 hover:text-white'
              } ${isMode ? `ring-1 ring-inset ${isActive ? 'ring-lime-400/60' : 'ring-white/15'}` : ''} ${
                item.disabled ? 'opacity-40 hover:bg-transparent hover:text-gray-300' : ''
              }`}
            >
              <span className="relative flex h-5 w-5 items-center justify-center">
                {item.icon}
                {item.badge && <Badge badge={item.badge} />}
              </span>
              <span className="w-full truncate px-0.5 text-center text-[10.5px] leading-tight font-medium">
                {item.label}
              </span>
            </button>
          </span>
        </Tooltip>
      </div>
    );
  };

  const indexed = items.map((item, index) => ({ item, index }));
  const modeItems = indexed.filter(({ item }) => item.group === 'mode');

  return (
    <nav
      aria-label={t('workspace.panel.sections')}
      className="w-16 shrink-0 h-full flex flex-col items-center gap-1 py-2 bg-[#161616] border-l border-white/10"
    >
      {indexed.filter(({ item }) => item.group !== 'mode').map(({ item, index }) => renderItem(item, index))}

      {modeItems.length > 0 && (
        <div className="mt-auto flex flex-col items-center gap-1">
          <div aria-hidden className="my-1 h-px w-8 shrink-0 bg-white/10" />
          {modeItems.map(({ item, index }) => renderItem(item, index))}
        </div>
      )}

      <Tooltip
        placement="left"
        arrow
        title={`${open ? t('workspace.panel.hidePanel') : t('workspace.panel.showPanel')}  ${TOGGLE_SHORTCUT}`}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-label={open ? t('workspace.panel.hidePanel') : t('workspace.panel.showPanel')}
          aria-expanded={open}
          className={`${modeItems.length > 0 ? 'mt-1' : 'mt-auto'} w-10 h-10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/5 transition-colors`}
        >
          {open ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
        </button>
      </Tooltip>
    </nav>
  );
}
